import React, {useCallback} from "react";
import {useParams} from "react-router";
import {MutationResult} from "@apollo/client";
import {useDiaEditor} from "../../components/DiaEditor";
import {NonIdealState} from "../../components/NonIdealState";
import WaitSpinner from "../../components/wait-spinner";
import * as yup from "yup";
import {
    CreateContractTemplateInput,
    CreateContractTemplateMutation,
    GetContractTemplateQueryQueryResult,
    UpdateContractTemplateInput,
    UpdateContractTemplateMutation,
    useCreateContractTemplateMutation,
    useGetContractTemplateQueryQuery,
    useUpdateContractTemplateMutation
} from "../../generated/graphql";
import {ContractSectionValue} from "../../components/contract/ContractDocument";
import ContractTemplateForm from "./Form";

export type ContractTemplateType = {
    id?: string | null,
    name?: string | null,
    description?: string | null,
    sections?: ContractSectionValue[] | null
};

const validationSchema = yup.object().shape({
    name: yup.string().min(1)
});

const relationships = {
    sections: 'create',
    'sections.blocks': 'create'
};

function blankValues(): ContractTemplateType {
    return {id: 'new', name: '', description: '', sections: []};
}

export function ContractTemplateEditor() {
    const params = useParams();
    const isNew = params.id == 'new';
    const query = useGetContractTemplateQueryQuery({variables: {id: params.id ?? ''}, skip: isNew});
    const [saveMutation, saveResult] = useUpdateContractTemplateMutation();
    const [createMutation, createResult] = useCreateContractTemplateMutation();
    const save = useCallback(({values}: {values: ContractTemplateType}) => {
        return saveMutation({variables: {input: values as UpdateContractTemplateInput}});
    }, [saveMutation]);
    const create = useCallback(({values}: {values: ContractTemplateType}) => {
        return createMutation({variables: {input: values as CreateContractTemplateInput}});
    }, [createMutation]);
    const getValue = useCallback((result: GetContractTemplateQueryQueryResult) => {
        const template = result?.data?.getContractTemplate;
        if (!template) return null;
        return {id: template.id, name: template.name, description: template.description, sections: template.sections ?? []};
    }, []);
    const getSaveValue = useCallback((result: MutationResult<UpdateContractTemplateMutation>) => {
        return result?.data?.updateContractTemplate ?? null;
    }, []);
    const getCreateRedirect = useCallback(({data, baseUrl}: {
        data: MutationResult<CreateContractTemplateMutation>,
        baseUrl: string,
        search: string
    }) => {
        return baseUrl + (data?.data?.createContractTemplate?.id ?? 'new');
    }, []);

    const data = useDiaEditor<ContractTemplateType, GetContractTemplateQueryQueryResult, CreateContractTemplateMutation, UpdateContractTemplateMutation>({
        query,
        save,
        saveResult,
        getSaveValue,
        create,
        createResult,
        blankValues,
        label: 'Contract template',
        permission: 'contracts.edit',
        validationSchema,
        relationships,
        baseUrl: '/database/contracts/',
        getValue,
        getCreateRedirect
    });

    if (query.error) {
        return <NonIdealState severity="error" title="An error has occurred" message="GraphQL error encountered"/>;
    }
    if (data.form.status == 'loading') {
        return <WaitSpinner variant="panel"/>;
    }
    return (
        <>
            {data.confirmation}
            <ContractTemplateForm waiting={data.waiting} diaForm={data.form}/>
        </>
    );
}

export default ContractTemplateEditor;
